import {
  Button,
  Card,
  Dialog,
  DialogPanel,
  Tab,
  TabGroup,
  TabList,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  TextInput,
  Textarea,
} from "@tremor/react";
import Badge from "../components/Badge.jsx";
import ProBadge from "../components/ProBadge.jsx";
import { usePlanGate } from "../lib/usePlan.js";
import {
  fetchPostmortem,
  createPostmortem,
  updatePostmortem,
  addCorrectiveAction,
  updateCorrectiveAction,
  removeCorrectiveAction,
} from "../api/postmortems.js";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Bell, Globe, MapPin, Plus, X } from "lucide-react";
import { toast } from "sonner";
// Child components (drawer, tag editor, RCA editor, note form) still call
// these directly — they pass the updated incident back up via callbacks,
// and the top-level page invalidates its cache via the hook layer there.
import {
  acknowledgeIncident,
  addChecklistItem,
  addIncidentResponder,
  addIncidentStakeholder,
  applyChecklistTemplate,
  createIncident,
  endCollaboration,
  exportIncidentsCsv,
  fetchIncident,
  fetchIncidents,
  fetchIncidentTemplates,
  fetchIncidentTimeline,
  notifyIncidentStakeholders,
  removeChecklistItem,
  removeIncidentResponder,
  removeIncidentStakeholder,
  reopenIncident,
  resolveIncident,
  startCollaboration,
  unacknowledgeIncident,
  updateChecklistItem,
  updateIncident,
} from "../api/incidents.js";
import { useIncidentAnalytics } from "../queries/incidents.js";
import { useMembers } from "../queries/workspaces.js";
import { fetchAlertNotifications } from "../api/alerts.js";
import IncidentDiagnosis from "../components/IncidentDiagnosis.jsx";
import IncidentExplainer from "../components/IncidentExplainer.jsx";
import { fmtDate, fmtDateTime } from "../utils/format.js";
import { createStatusPageMessage, fetchStatusPages } from "../api/statusPages.js";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STATUS_TABS = [
  { label: "All", value: "" },
  { label: "Open", value: "open" },
  { label: "Acknowledged", value: "acknowledged" },
  { label: "Investigating", value: "investigating" },
  { label: "Resolved", value: "resolved" },
];

const SEVERITY_OPTIONS = ["critical", "high", "medium", "low"];
const PRIORITY_OPTIONS = ["P1", "P2", "P3", "P4", "P5"];
const RESPONDER_ROLES = [
  "commander",
  "communications_lead",
  "technical_lead",
  "scribe",
  "responder",
];

const SEVERITY_BADGE_COLOR = { critical: "red", high: "orange", medium: "yellow", low: "teal" };
const PRIORITY_BADGE_COLOR = { P1: "red", P2: "orange", P3: "yellow", P4: "teal", P5: "gray" };
const STATUS_BADGE_COLOR = { open: "red", acknowledged: "amber", investigating: "teal", resolved: "emerald" };

const EVENT_ICON = {
  failure_detected: "🔴",
  recovery_detected: "🟢",
  incident_opened: "🚨",
  acknowledged: "👀",
  assigned: "👤",
  status_changed: "🔄",
  severity_changed: "⚡",
  alert_sent: "📣",
  note_added: "📝",
  location_update: "📍",
  tags_updated: "🏷️",
  rca_added: "📋",
  grouped: "🔗",
  resolved: "✅",
  status_page_update: "📢",
  diagnostic_snapshot: "🔬",
  artifact_captured: "🖼️",
  third_party_impact: "⚠️",
  priority_changed: "🔺",
  responder_assigned: "🙋",
  responder_removed: "👋",
  stakeholder_added: "👥",
  stakeholder_removed: "🚪",
  stakeholder_update_sent: "✉️",
  checklist_item_added: "☑️",
  checklist_item_checked: "✅",
  checklist_item_unchecked: "⬜",
  template_applied: "📋",
  collaboration_started: "🎥",
  collaboration_ended: "🔚",
  postmortem_created: "📄",
  postmortem_status_changed: "🔖",
  postmortem_action_added: "✔️",
};

import { useTranslation } from "react-i18next";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtDuration(seconds, t) {
  if (seconds == null) return t("duration.ongoing");
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  if (m >= 60) return t("duration.hours_minutes", { h: Math.floor(m / 60), m: m % 60 });
  if (m > 0) return t("duration.minutes_seconds", { m, s });
  return t("duration.seconds", { s });
}

function fmtTs(iso) {
  if (!iso) return "—";
  return fmtDateTime(iso);
}

function fmtRelative(iso, t) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return t("relative.just_now");
  if (m < 60) return t("relative.minutes_ago", { count: m });
  const h = Math.floor(m / 60);
  if (h < 24) return t("relative.hours_ago", { count: h });
  return t("relative.days_ago", { count: Math.floor(h / 24) });
}

function StatCard({ label, value, sub, color = "text-gray-900" }) {
  return (
    <Card className="py-4 px-5">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-400">{label}</p>
      <p className={`mt-1 text-2xl font-bold ${color}`}>{value ?? "—"}</p>
      {sub && <p className="mt-0.5 text-xs text-gray-400">{sub}</p>}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Create incident modal
// ---------------------------------------------------------------------------

function CreateIncidentModal({ onClose, onCreated }) {
  const { t } = useTranslation("incidents");
  const [form, setForm] = useState({ title: "", description: "", severity: "high", priority: "" });
  const [saving, setSaving] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const incident = await createIncident({
        title: form.title,
        description: form.description || undefined,
        severity: form.severity,
        priority: form.priority || undefined,
      });
      toast.success(t("modal.success"));
      onCreated(incident);
      onClose();
    } catch {
      toast.error("Failed to create incident");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={onClose}>
      <DialogPanel className="w-full max-w-md">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">{t("modal.title")}</h2>
          <button type="button" onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100">
            <X className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">{t("modal.field_title")}</label>
            <TextInput
              required
              value={form.title}
              onValueChange={(v) => setForm((f) => ({ ...f, title: v }))}
              placeholder={t("modal.title_placeholder")}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">{t("modal.description")}</label>
            <Textarea
              rows={3}
              value={form.description}
              onValueChange={(v) => setForm((f) => ({ ...f, description: v }))}
              placeholder={t("modal.description_placeholder")}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">{t("modal.severity")}</label>
            <select
              value={form.severity}
              onChange={(e) => setForm((f) => ({ ...f, severity: e.target.value }))}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
            >
              {SEVERITY_OPTIONS.map((s) => (
                <option key={s} value={s}>{t(`filter.${s}`)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">{t("modal.priority")}</label>
            <select
              value={form.priority}
              onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
            >
              <option value="">{t("modal.priority_auto")}</option>
              {PRIORITY_OPTIONS.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" type="button" onClick={onClose}>{t("common:common.cancel")}</Button>
            <Button type="submit" color="red" disabled={saving}>
              {saving ? t("modal.creating") : t("modal.create")}
            </Button>
          </div>
        </form>
      </DialogPanel>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Artifact and Impact panels
// ---------------------------------------------------------------------------

function ArtifactPanel({ data }) {
  const { t } = useTranslation("incidents");
  if (!data) return null;
  const { screenshot_url, har_url, screenshots } = data;

  return (
    <div className="mt-2 space-y-2">
      {screenshot_url && (
        <a href={screenshot_url} target="_blank" rel="noreferrer" className="block overflow-hidden rounded border border-gray-200 shadow-sm transition-opacity hover:opacity-90">
          <img src={screenshot_url} alt="Failure screenshot" className="w-full" />
        </a>
      )}
      {screenshots?.length > 0 && (
        <div className="grid grid-cols-2 gap-2">
          {screenshots.map((s, i) => (
            s.url && (
              <a key={i} href={s.url} target="_blank" rel="noreferrer" className="block overflow-hidden rounded border border-gray-100 shadow-sm hover:opacity-90">
                <img src={s.url} alt={s.label} className="w-full" />
                <p className="bg-gray-50 px-2 py-1 text-[10px] text-gray-500 truncate">{s.label || `Shot ${i+1}`}</p>
              </a>
            )
          ))}
        </div>
      )}
      {har_url && (
        <a
          href={`/${har_url}`}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-xs text-teal-600 hover:underline"
        >
          <Globe className="h-3 w-3" /> {t("artifacts.view_har")}
        </a>
      )}
    </div>
  );
}

function ImpactPanel({ data }) {
  const { t } = useTranslation("incidents");
  const impact = data?.impact;
  if (!Array.isArray(impact)) return null;

  const high = impact.filter((i) => i.impact === "high");
  if (high.length === 0) return null;

  return (
    <div className="mt-2 rounded border border-orange-100 bg-orange-50 p-3">
      <p className="text-[10px] font-bold uppercase tracking-wider text-orange-800">{t("artifacts.third_party")}</p>
      <ul className="mt-1 space-y-1">
        {high.map((item, i) => (
          <li key={i} className="flex items-center justify-between text-xs text-orange-900">
            <span className="font-medium">{item.provider}</span>
            <span className="text-[10px] opacity-70">{item.domain}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Diagnostic snapshot panel
// ---------------------------------------------------------------------------

function DiagnosticPanel({ data }) {
  const { t } = useTranslation("incidents");
  const [open, setOpen] = useState(false);
  if (!data) return null;

  const probes = [
    { key: "dns", label: "DNS" },
    { key: "tcp", label: "TCP" },
    { key: "tls", label: "TLS" },
    { key: "http", label: "HTTP" },
  ];

  return (
    <div className="mt-1">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="text-xs text-teal-600 hover:underline"
      >
        {open ? t("drawer.hide_details") : t("drawer.show_details")}
      </button>
      {open && (
        <div className="mt-2 space-y-1.5 rounded border border-gray-200 bg-gray-50 p-3">
          {probes.map(({ key, label }) => {
            const probe = data[key];
            if (!probe) return null;
            if (probe.skipped)
              return (
                <div key={key} className="flex items-center gap-2 text-xs text-gray-400">
                  <span className="w-8 font-medium">{label}</span>
                  <span>{t("drawer.skipped")}</span>
                </div>
              );
            return (
              <div key={key} className="flex items-start gap-2 text-xs">
                <span
                  className={[
                    "w-8 font-medium",
                    probe.ok === false ? "text-red-600" : "text-green-600",
                  ].join(" ")}
                >
                  {label}
                </span>
                <span className="text-gray-600">
                  {probe.ok === false
                    ? probe.error || t("drawer.failed")
                    : probe.duration_ms != null
                    ? `${probe.duration_ms}ms`
                    : t("drawer.ok")}
                  {key === "tls" && probe.cert_expiry_days != null && ` · ${t("drawer.cert_expires", { count: probe.cert_expiry_days })}`}
                  {key === "dns" && probe.ips?.length > 0 && ` · ${probe.ips.join(", ")}`}
                  {key === "http" && probe.status_code && ` · HTTP ${probe.status_code}`}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add note form (inside detail drawer)
// ---------------------------------------------------------------------------

function AddNoteForm({ incidentId, onAdded }) {
  const { t } = useTranslation("incidents");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!note.trim()) return;
    setSaving(true);
    try {
      await updateIncident(incidentId, { note: note.trim() });
      toast.success(t("notes.success"));
      setNote("");
      onAdded();
    } catch {
      toast.error("Failed to add note");
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="mt-4 flex gap-2">
      <TextInput
        value={note}
        onValueChange={setNote}
        placeholder={t("notes.placeholder")}
        className="flex-1"
      />
      <Button type="submit" size="xs" variant="secondary" disabled={saving || !note.trim()}>
        {saving ? "…" : t("notes.add")}
      </Button>
    </form>
  );
}

function PostStatusUpdateForm({ incident, onAdded }) {
  const { t } = useTranslation("incidents");
  const [statusPages, setStatusPages] = useState([]);
  const [statusPageId, setStatusPageId] = useState("");
  const [title, setTitle] = useState(incident.title || "");
  const [body, setBody] = useState("");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchStatusPages().then((pages) => {
      setStatusPages(pages);
      const match = pages.find((p) => p.monitor_ids?.includes(incident.monitor_id));
      setStatusPageId((match || pages[0])?.id || "");
    });
  }, [incident.monitor_id]);

  if (statusPages.length === 0) return null;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!statusPageId || !title.trim() || !body.trim()) return;
    setSaving(true);
    try {
      await createStatusPageMessage(statusPageId, {
        title: title.trim(),
        body: body.trim(),
        is_active: true,
        publish: true,
        incident_id: incident.id,
      });
      toast.success(t("status_update.success"));
      setBody("");
      setOpen(false);
      onAdded();
    } catch (err) {
      toast.error(err.response?.data?.error ?? "Failed to post status update");
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-4 text-xs font-medium text-teal-600 hover:underline"
      >
        {t("status_update.open")}
      </button>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="mt-4 space-y-2 rounded-md border border-gray-200 p-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
        {t("status_update.title_label")}
      </p>
      {statusPages.length > 1 && (
        <select
          value={statusPageId}
          onChange={(e) => setStatusPageId(e.target.value)}
          className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm outline-none focus:border-teal-600"
        >
          {statusPages.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      )}
      <TextInput value={title} onValueChange={setTitle} placeholder={t("status_update.title_placeholder")} />
      <Textarea value={body} onValueChange={setBody} rows={3} placeholder={t("status_update.body_placeholder")} />
      <div className="flex justify-end gap-2">
        <Button type="button" size="xs" variant="secondary" onClick={() => setOpen(false)}>
          {t("status_update.cancel")}
        </Button>
        <Button type="submit" size="xs" disabled={saving || !title.trim() || !body.trim()}>
          {saving ? t("status_update.saving") : t("status_update.publish")}
        </Button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Postmortem (distinct from the quick RcaEditor note above -- a formal,
// structured document with a review/approval workflow and tracked
// corrective actions)
// ---------------------------------------------------------------------------

const POSTMORTEM_STATUSES = ["draft", "in_review", "approved", "published"];
const ACTION_STATUSES = ["open", "in_progress", "done", "cancelled"];
const POSTMORTEM_FIELDS = [
  "executive_summary",
  "what_happened",
  "contributing_factors",
  "what_went_well",
  "what_went_poorly",
];

function PostmortemPanel({ incidentId, postmortem, onChanged }) {
  const { t } = useTranslation("incidents");
  const [open, setOpen] = useState(postmortem.status !== "published");
  const [fields, setFields] = useState(() =>
    Object.fromEntries(POSTMORTEM_FIELDS.map((k) => [k, postmortem[k] || ""]))
  );
  const [newActionTitle, setNewActionTitle] = useState("");

  useEffect(() => {
    setFields(Object.fromEntries(POSTMORTEM_FIELDS.map((k) => [k, postmortem[k] || ""])));
  }, [postmortem.id]);

  async function saveField(key) {
    if (fields[key] === (postmortem[key] || "")) return;
    try {
      const updated = await updatePostmortem(incidentId, { [key]: fields[key] });
      onChanged(updated);
    } catch {
      toast.error("Failed to save postmortem");
    }
  }

  async function handleStatusChange(newStatus) {
    try {
      const updated = await updatePostmortem(incidentId, { status: newStatus });
      onChanged(updated);
    } catch (err) {
      toast.error(err.response?.data?.error ?? "Failed to update status");
    }
  }

  async function handleAddAction() {
    if (!newActionTitle.trim()) return;
    try {
      const action = await addCorrectiveAction(incidentId, { title: newActionTitle.trim() });
      onChanged({ ...postmortem, corrective_actions: [...postmortem.corrective_actions, action] });
      setNewActionTitle("");
    } catch (err) {
      toast.error(err.response?.data?.error ?? "Failed to add corrective action");
    }
  }

  async function handleActionStatus(actionId, status) {
    try {
      const updated = await updateCorrectiveAction(incidentId, actionId, { status });
      onChanged({
        ...postmortem,
        corrective_actions: postmortem.corrective_actions.map((a) =>
          a.id === actionId ? updated : a
        ),
      });
    } catch {
      toast.error("Failed to update corrective action");
    }
  }

  async function handleRemoveAction(actionId) {
    try {
      await removeCorrectiveAction(incidentId, actionId);
      onChanged({
        ...postmortem,
        corrective_actions: postmortem.corrective_actions.filter((a) => a.id !== actionId),
      });
    } catch {
      toast.error("Failed to remove corrective action");
    }
  }

  const statusIdx = POSTMORTEM_STATUSES.indexOf(postmortem.status);
  const nextStatus = POSTMORTEM_STATUSES[statusIdx + 1];

  return (
    <div className="border-b border-gray-100 px-6 py-3">
      <div className="mb-1.5 flex items-center justify-between">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-1.5 text-xs font-medium text-gray-400"
        >
          {t("drawer.postmortem")}
          <Badge color={postmortem.status === "published" ? "emerald" : "gray"} size="xs">
            {t(`postmortem_statuses.${postmortem.status}`)}
          </Badge>
        </button>
        {nextStatus && (
          <button
            type="button"
            onClick={() => handleStatusChange(nextStatus)}
            className="text-xs text-teal-700 hover:underline"
          >
            {t("drawer.postmortem_advance", { status: t(`postmortem_statuses.${nextStatus}`) })}
          </button>
        )}
      </div>
      {open && (
        <div className="space-y-2">
          {POSTMORTEM_FIELDS.map((key) => (
            <div key={key}>
              <label className="mb-0.5 block text-xs text-gray-400">
                {t(`postmortem_fields.${key}`)}
              </label>
              <Textarea
                value={fields[key]}
                onValueChange={(v) => setFields((f) => ({ ...f, [key]: v }))}
                onBlur={() => saveField(key)}
                rows={2}
              />
            </div>
          ))}

          <div>
            <p className="mb-1 text-xs text-gray-400">{t("drawer.corrective_actions")}</p>
            {postmortem.corrective_actions.length > 0 && (
              <ul className="mb-1.5 space-y-1">
                {postmortem.corrective_actions.map((a) => (
                  <li key={a.id} className="flex items-center gap-2 text-sm">
                    <select
                      value={a.status}
                      onChange={(e) => handleActionStatus(a.id, e.target.value)}
                      className="rounded border border-gray-300 px-1 py-0.5 text-xs outline-none"
                    >
                      {ACTION_STATUSES.map((s) => (
                        <option key={s} value={s}>{t(`action_statuses.${s}`)}</option>
                      ))}
                    </select>
                    <span
                      className={
                        a.status === "done"
                          ? "flex-1 text-gray-400 line-through"
                          : "flex-1 text-gray-700"
                      }
                    >
                      {a.title}
                    </span>
                    {a.due_date && <span className="shrink-0 text-xs text-gray-400">{a.due_date}</span>}
                    <button
                      type="button"
                      onClick={() => handleRemoveAction(a.id)}
                      className="shrink-0 text-gray-400 hover:text-red-500"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex items-center gap-2">
              <TextInput
                value={newActionTitle}
                onValueChange={setNewActionTitle}
                placeholder={t("drawer.corrective_action_placeholder")}
                className="flex-1"
              />
              <Button size="xs" disabled={!newActionTitle.trim()} onClick={handleAddAction}>
                {t("drawer.add")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PostmortemEditor({ incident }) {
  const { t } = useTranslation("incidents");
  const allows = usePlanGate();
  const allowed = allows("operations");
  const [postmortem, setPostmortem] = useState(null);
  const [loading, setLoading] = useState(allowed);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!allowed) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPostmortem(incident.id)
      .then(setPostmortem)
      .catch(() => setPostmortem(null))
      .finally(() => setLoading(false));
  }, [incident.id, allowed]);

  async function handleCreate() {
    setCreating(true);
    try {
      const created = await createPostmortem(incident.id, {});
      setPostmortem(created);
    } catch (err) {
      toast.error(err.response?.data?.error ?? "Failed to create postmortem");
    } finally {
      setCreating(false);
    }
  }

  if (!allowed) {
    return (
      <div className="border-b border-gray-100 px-6 py-3">
        <p className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-gray-400">
          {t("drawer.postmortem")}
          <ProBadge />
        </p>
        <p className="text-xs text-gray-400">{t("drawer.operations_upgrade_note")}</p>
      </div>
    );
  }

  if (loading) return null;

  if (postmortem) {
    return (
      <PostmortemPanel incidentId={incident.id} postmortem={postmortem} onChanged={setPostmortem} />
    );
  }

  return (
    <div className="border-b border-gray-100 px-6 py-3">
      <p className="mb-1.5 text-xs font-medium text-gray-400">{t("drawer.postmortem")}</p>
      {incident.status === "resolved" ? (
        <Button size="xs" disabled={creating} onClick={handleCreate}>
          {t("drawer.postmortem_create")}
        </Button>
      ) : (
        <p className="text-xs text-gray-400">{t("drawer.postmortem_needs_resolved")}</p>
      )}
    </div>
  );
}

function RcaEditor({ incident, onUpdated }) {
  const { t } = useTranslation("incidents");
  const [text, setText] = useState(incident.rca_summary || "");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Sync if a different incident is shown
  useEffect(() => {
    setText(incident.rca_summary || "");
    setDirty(false);
  }, [incident.id, incident.rca_summary]);

  async function save() {
    if (!dirty) return;
    setSaving(true);
    try {
      const updated = await updateIncident(incident.id, { rca_summary: text });
      onUpdated(updated);
      setDirty(false);
      toast.success(t("rca.success"));
    } catch {
      toast.error("Failed to save post-mortem");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="border-b border-gray-100 px-6 py-4">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
          {t("rca.title")}
        </p>
        {dirty && (
          <Button size="xs" type="button" onClick={save} disabled={saving}>
            {saving ? t("rca.saving") : t("rca.save")}
          </Button>
        )}
      </div>
      <Textarea
        value={text}
        onValueChange={(v) => { setText(v); setDirty(true); }}
        onBlur={save}
        rows={4}
        placeholder={t("rca.placeholder")}
      />
      {incident.rca_at && (
        <p className="mt-1 text-xs text-gray-400">
          {t("rca.last_updated", { date: fmtTs(incident.rca_at) })}
        </p>
      )}
    </div>
  );
}

function TagEditor({ incident, onUpdated }) {
  const { t } = useTranslation("incidents");
  const [tags, setTags] = useState(incident.tags || []);
  const [input, setInput] = useState("");
  const [saving, setSaving] = useState(false);

  async function addTag(tag) {
    tag = tag.trim().toLowerCase();
    if (!tag || tags.includes(tag)) return;
    const next = [...tags, tag];
    setTags(next);
    setInput("");
    setSaving(true);
    try {
      const updated = await updateIncident(incident.id, { tags: next });
      onUpdated(updated);
    } catch {
      setTags(tags); // rollback
      toast.error("Failed to update tags");
    } finally {
      setSaving(false);
    }
  }

  async function removeTag(tag) {
    const next = tags.filter((t) => t !== tag);
    setTags(next);
    setSaving(true);
    try {
      const updated = await updateIncident(incident.id, { tags: next });
      onUpdated(updated);
    } catch {
      setTags(tags);
      toast.error("Failed to update tags");
    } finally {
      setSaving(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag(input);
    } else if (e.key === "Backspace" && !input && tags.length > 0) {
      removeTag(tags[tags.length - 1]);
    }
  }

  return (
    <div className="border-b border-gray-100 px-6 py-3">
      <p className="mb-1.5 text-xs font-medium text-gray-400">{t("drawer.tags")}</p>
      <div className="flex flex-wrap items-center gap-1.5">
        {tags.map((tag) => (
          <Badge key={tag} color="gray">
            {tag}
            <button
              type="button"
              onClick={() => removeTag(tag)}
              disabled={saving}
              className="ml-1 text-gray-400 hover:text-gray-700 disabled:opacity-50"
            >
              ×
            </button>
          </Badge>
        ))}
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => input.trim() && addTag(input)}
          placeholder={tags.length === 0 ? t("drawer.add_tags") : ""}
          className="min-w-[80px] flex-1 border-none bg-transparent text-xs outline-none placeholder-gray-300"
        />
      </div>
    </div>
  );
}

function ResponderEditor({ incidentId, responders, onAdded, onRemoved }) {
  const { t } = useTranslation("incidents");
  const allows = usePlanGate();
  const allowed = allows("operations");
  const membersQuery = useMembers();
  const members = membersQuery.data ?? [];
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedRole, setSelectedRole] = useState("responder");
  const [saving, setSaving] = useState(false);

  const availableMembers = members.filter(
    (m) => !responders.some((r) => r.user_id === m.user_id)
  );

  async function handleAdd() {
    if (!selectedUserId) return;
    setSaving(true);
    try {
      const created = await addIncidentResponder(incidentId, {
        user_id: selectedUserId,
        role: selectedRole,
      });
      onAdded(created);
      setSelectedUserId("");
      setSelectedRole("responder");
    } catch (err) {
      toast.error(err.response?.data?.error ?? "Failed to add responder");
    } finally {
      setSaving(false);
    }
  }

  async function handleRemove(userId) {
    try {
      await removeIncidentResponder(incidentId, userId);
      onRemoved(userId);
    } catch {
      toast.error("Failed to remove responder");
    }
  }

  return (
    <div className="border-b border-gray-100 px-6 py-3">
      <p className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-gray-400">
        {t("drawer.responders")}
        {!allowed && <ProBadge />}
      </p>
      {responders.length > 0 && (
        <ul className="mb-2 space-y-1">
          {responders.map((r) => (
            <li key={r.user_id} className="flex items-center justify-between gap-2 text-sm">
              <span className="flex items-center gap-1.5">
                <Badge color="slate" size="xs">{t(`responder_roles.${r.role}`)}</Badge>
                <span className="text-gray-700">{r.user_name || r.user_email}</span>
              </span>
              {allowed && (
                <button
                  type="button"
                  onClick={() => handleRemove(r.user_id)}
                  className="text-gray-400 hover:text-red-500"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {!allowed ? (
        <p className="text-xs text-gray-400">{t("drawer.operations_upgrade_note")}</p>
      ) : (
        availableMembers.length > 0 && (
          <div className="flex items-center gap-2">
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="flex-1 rounded-md border border-gray-300 px-2 py-1 text-xs outline-none focus:border-teal-600"
            >
              <option value="">{t("drawer.add_responder_placeholder")}</option>
              {availableMembers.map((m) => (
                <option key={m.user_id} value={m.user_id}>{m.full_name || m.email}</option>
              ))}
            </select>
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="rounded-md border border-gray-300 px-2 py-1 text-xs outline-none focus:border-teal-600"
            >
              {RESPONDER_ROLES.map((role) => (
                <option key={role} value={role}>{t(`responder_roles.${role}`)}</option>
              ))}
            </select>
            <Button size="xs" disabled={!selectedUserId || saving} onClick={handleAdd}>
              {t("drawer.add")}
            </Button>
          </div>
        )
      )}
    </div>
  );
}

function StakeholderEditor({ incidentId, stakeholders, onAdded, onRemoved }) {
  const { t } = useTranslation("incidents");
  const allows = usePlanGate();
  const allowed = allows("operations");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  async function handleAdd() {
    if (!email.trim()) return;
    setSaving(true);
    try {
      const created = await addIncidentStakeholder(incidentId, {
        email: email.trim(),
        name: name.trim() || undefined,
      });
      onAdded(created);
      setEmail("");
      setName("");
    } catch (err) {
      toast.error(err.response?.data?.error ?? "Failed to add stakeholder");
    } finally {
      setSaving(false);
    }
  }

  async function handleRemove(stakeholderId) {
    try {
      await removeIncidentStakeholder(incidentId, stakeholderId);
      onRemoved(stakeholderId);
    } catch {
      toast.error("Failed to remove stakeholder");
    }
  }

  async function handleNotify() {
    if (!message.trim()) return;
    setSending(true);
    try {
      const res = await notifyIncidentStakeholders(incidentId, message.trim());
      toast.success(t("drawer.stakeholder_notify_success", { count: res.sent_count }));
      setMessage("");
    } catch (err) {
      toast.error(err.response?.data?.error ?? "Failed to notify stakeholders");
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="border-b border-gray-100 px-6 py-3">
      <p className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-gray-400">
        {t("drawer.stakeholders")}
        {!allowed && <ProBadge />}
      </p>
      {stakeholders.length > 0 && (
        <ul className="mb-2 space-y-1">
          {stakeholders.map((s) => (
            <li key={s.id} className="flex items-center justify-between gap-2 text-sm">
              <span className="truncate text-gray-700">
                {s.name ? `${s.name} <${s.email}>` : s.email}
              </span>
              {allowed && (
                <button
                  type="button"
                  onClick={() => handleRemove(s.id)}
                  className="shrink-0 text-gray-400 hover:text-red-500"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {!allowed ? (
        <p className="text-xs text-gray-400">{t("drawer.operations_upgrade_note")}</p>
      ) : (
        <>
          <div className="flex items-center gap-2">
            <TextInput
              value={email}
              onValueChange={setEmail}
              placeholder={t("drawer.stakeholder_email_placeholder")}
              className="flex-1"
            />
            <TextInput
              value={name}
              onValueChange={setName}
              placeholder={t("drawer.stakeholder_name_placeholder")}
              className="w-32"
            />
            <Button size="xs" disabled={!email.trim() || saving} onClick={handleAdd}>
              {t("drawer.add")}
            </Button>
          </div>
          {stakeholders.length > 0 && (
            <div className="mt-2 flex items-center gap-2">
              <TextInput
                value={message}
                onValueChange={setMessage}
                placeholder={t("drawer.stakeholder_message_placeholder")}
                className="flex-1"
              />
              <Button size="xs" variant="secondary" disabled={!message.trim() || sending} onClick={handleNotify}>
                {t("drawer.notify")}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ChecklistEditor({ incidentId, checklist, onChanged }) {
  const { t } = useTranslation("incidents");
  const allows = usePlanGate();
  const allowed = allows("operations");
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!allowed) return;
    fetchIncidentTemplates().then(setTemplates).catch(() => {});
  }, [allowed]);

  async function handleAdd() {
    if (!text.trim()) return;
    setSaving(true);
    try {
      const created = await addChecklistItem(incidentId, text.trim());
      onChanged((prev) => [...prev, created]);
      setText("");
    } catch (err) {
      toast.error(err.response?.data?.error ?? "Failed to add checklist item");
    } finally {
      setSaving(false);
    }
  }

  async function handleToggle(item) {
    // optimistic
    onChanged((prev) =>
      prev.map((c) => (c.id === item.id ? { ...c, is_checked: !c.is_checked } : c))
    );
    try {
      const updated = await updateChecklistItem(incidentId, item.id, {
        is_checked: !item.is_checked,
      });
      onChanged((prev) => prev.map((c) => (c.id === item.id ? updated : c)));
    } catch {
      onChanged((prev) =>
        prev.map((c) => (c.id === item.id ? { ...c, is_checked: item.is_checked } : c))
      );
      toast.error("Failed to update checklist item");
    }
  }

  async function handleRemove(itemId) {
    try {
      await removeChecklistItem(incidentId, itemId);
      onChanged((prev) => prev.filter((c) => c.id !== itemId));
    } catch {
      toast.error("Failed to remove checklist item");
    }
  }

  async function handleApplyTemplate() {
    if (!selectedTemplateId) return;
    setApplying(true);
    try {
      const created = await applyChecklistTemplate(incidentId, selectedTemplateId);
      onChanged((prev) => [...prev, ...created]);
      setSelectedTemplateId("");
    } catch (err) {
      toast.error(err.response?.data?.error ?? "Failed to apply template");
    } finally {
      setApplying(false);
    }
  }

  const doneCount = checklist.filter((c) => c.is_checked).length;

  return (
    <div className="border-b border-gray-100 px-6 py-3">
      <p className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-gray-400">
        {t("drawer.checklist")}
        {checklist.length > 0 && (
          <span className="text-gray-300">({doneCount}/{checklist.length})</span>
        )}
        {!allowed && <ProBadge />}
      </p>
      {checklist.length > 0 && (
        <ul className="mb-2 space-y-1">
          {checklist.map((item) => (
            <li key={item.id} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={item.is_checked}
                onChange={() => allowed && handleToggle(item)}
                disabled={!allowed}
                className="h-3.5 w-3.5 rounded border-gray-300 text-teal-600"
              />
              <span className={item.is_checked ? "flex-1 text-gray-400 line-through" : "flex-1 text-gray-700"}>
                {item.text}
              </span>
              {allowed && (
                <button
                  type="button"
                  onClick={() => handleRemove(item.id)}
                  className="shrink-0 text-gray-400 hover:text-red-500"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {!allowed ? (
        <p className="text-xs text-gray-400">{t("drawer.operations_upgrade_note")}</p>
      ) : (
        <>
          <div className="flex items-center gap-2">
            <TextInput
              value={text}
              onValueChange={setText}
              placeholder={t("drawer.checklist_item_placeholder")}
              className="flex-1"
            />
            <Button size="xs" disabled={!text.trim() || saving} onClick={handleAdd}>
              {t("drawer.add")}
            </Button>
          </div>
          {templates.length > 0 && (
            <div className="mt-2 flex items-center gap-2">
              <select
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                className="flex-1 rounded-md border border-gray-300 px-2 py-1 text-xs outline-none focus:border-teal-600"
              >
                <option value="">{t("drawer.apply_template_placeholder")}</option>
                {templates.map((tpl) => (
                  <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
                ))}
              </select>
              <Button
                size="xs"
                variant="secondary"
                disabled={!selectedTemplateId || applying}
                onClick={handleApplyTemplate}
              >
                {t("drawer.apply")}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const COLLABORATION_PROVIDERS = ["slack", "teams", "zoom", "google_meet", "other"];

function CollaborationEditor({ incidentId, sessions, onChanged }) {
  const { t } = useTranslation("incidents");
  const allows = usePlanGate();
  const allowed = allows("operations");
  const [provider, setProvider] = useState("slack");
  const [bridgeUrl, setBridgeUrl] = useState("");
  const [label, setLabel] = useState("");
  const [starting, setStarting] = useState(false);

  async function handleStart() {
    if (!bridgeUrl.trim()) return;
    setStarting(true);
    try {
      const created = await startCollaboration(incidentId, {
        provider,
        bridge_url: bridgeUrl.trim(),
        label: label.trim() || undefined,
      });
      onChanged((prev) => [...prev, created]);
      setBridgeUrl("");
      setLabel("");
    } catch (err) {
      toast.error(err.response?.data?.error ?? "Failed to start collaboration session");
    } finally {
      setStarting(false);
    }
  }

  async function handleEnd(sessionId) {
    try {
      const updated = await endCollaboration(incidentId, sessionId);
      onChanged((prev) => prev.map((s) => (s.id === sessionId ? updated : s)));
    } catch {
      toast.error("Failed to close collaboration session");
    }
  }

  return (
    <div className="border-b border-gray-100 px-6 py-3">
      <p className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-gray-400">
        {t("drawer.collaboration")}
        {!allowed && <ProBadge />}
      </p>
      {sessions.length > 0 && (
        <ul className="mb-2 space-y-1">
          {sessions.map((s) => (
            <li key={s.id} className="flex items-center justify-between gap-2 text-sm">
              <span className="flex min-w-0 items-center gap-1.5">
                <Badge color={s.ended_at ? "gray" : "emerald"} size="xs">
                  {s.ended_at ? t("drawer.ended") : t("drawer.active")}
                </Badge>
                <a
                  href={s.bridge_url}
                  target="_blank"
                  rel="noreferrer"
                  className="truncate text-teal-700 hover:underline"
                >
                  {s.label || `${s.provider} bridge`}
                </a>
              </span>
              {allowed && !s.ended_at && (
                <button
                  type="button"
                  onClick={() => handleEnd(s.id)}
                  className="shrink-0 text-xs text-gray-400 hover:text-red-500"
                >
                  {t("drawer.end")}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {!allowed ? (
        <p className="text-xs text-gray-400">{t("drawer.operations_upgrade_note")}</p>
      ) : (
        <div className="flex items-center gap-2">
          <select
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
            className="rounded-md border border-gray-300 px-2 py-1 text-xs outline-none focus:border-teal-600"
          >
            {COLLABORATION_PROVIDERS.map((p) => (
              <option key={p} value={p}>{t(`collaboration_providers.${p}`)}</option>
            ))}
          </select>
          <TextInput
            value={bridgeUrl}
            onValueChange={setBridgeUrl}
            placeholder={t("drawer.bridge_url_placeholder")}
            className="flex-1"
          />
          <TextInput
            value={label}
            onValueChange={setLabel}
            placeholder={t("drawer.bridge_label_placeholder")}
            className="w-28"
          />
          <Button size="xs" disabled={!bridgeUrl.trim() || starting} onClick={handleStart}>
            {t("drawer.start")}
          </Button>
        </div>
      )}
    </div>
  );
}

function IncidentDrawer({ incident, onClose, onUpdated }) {
  const { t } = useTranslation(["incidents", "common"]);
  const [timeline, setTimeline] = useState(incident.timeline || []);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [acting, setActing] = useState(false);
  const [totalFires, setTotalFires] = useState(null);
  const [responders, setResponders] = useState(incident.responders || []);
  const [stakeholders, setStakeholders] = useState(incident.stakeholders || []);
  const [checklist, setChecklist] = useState(incident.checklist || []);
  const [collaborationSessions, setCollaborationSessions] = useState(
    incident.collaboration_sessions || []
  );

  // Load full timeline on open
  useEffect(() => {
    fetchIncidentTimeline(incident.id, { limit: 50 }).then((res) => {
      setTimeline(res.data);
      setNextCursor(res.meta.next_cursor);
    });
  }, [incident.id]);

  // Load responders + stakeholders on open — list rows (unlike get_incident)
  // don't embed them
  useEffect(() => {
    fetchIncident(incident.id).then((full) => {
      setResponders(full.responders || []);
      setStakeholders(full.stakeholders || []);
      setChecklist(full.checklist || []);
      setCollaborationSessions(full.collaboration_sessions || []);
    });
  }, [incident.id]);

  // Load alert fire counts
  useEffect(() => {
    if (!incident.monitor_id) return;
    fetchAlertNotifications(incident.monitor_id)
      .then((res) => setTotalFires(res.meta.total_fires))
      .catch(() => {});
  }, [incident.monitor_id]);

  async function loadMore() {
    if (!nextCursor) return;
    setLoadingMore(true);
    try {
      const res = await fetchIncidentTimeline(incident.id, { cursor: nextCursor, limit: 50 });
      setTimeline((prev) => [...prev, ...res.data]);
      setNextCursor(res.meta.next_cursor);
    } finally {
      setLoadingMore(false);
    }
  }

  async function handleAcknowledge() {
    setActing(true);
    try {
      const updated = await acknowledgeIncident(incident.id);
      toast.success(t("actions.success_ack"));
      onUpdated(updated);
    } catch {
      toast.error("Failed to acknowledge");
    } finally {
      setActing(false);
    }
  }

  async function handleResolve() {
    setActing(true);
    try {
      const updated = await resolveIncident(incident.id);
      toast.success(t("actions.success_resolve"));
      onUpdated(updated);
    } catch {
      toast.error("Failed to resolve");
    } finally {
      setActing(false);
    }
  }

  async function handleUnacknowledge() {
    setActing(true);
    try {
      const updated = await unacknowledgeIncident(incident.id);
      toast.success(t("actions.success_unack"));
      onUpdated(updated);
    } catch {
      toast.error("Failed to unacknowledge");
    } finally {
      setActing(false);
    }
  }

  async function handleReopen() {
    setActing(true);
    try {
      const updated = await reopenIncident(incident.id);
      toast.success(t("actions.success_reopen"));
      onUpdated(updated);
    } catch {
      toast.error("Failed to reopen");
    } finally {
      setActing(false);
    }
  }

  async function handleStatusChange(newStatus) {
    setActing(true);
    try {
      const updated = await updateIncident(incident.id, { status: newStatus });
      toast.success(`${t("table.status")} → ${t(`filter.${newStatus}`)}`);
      onUpdated(updated);
    } catch {
      toast.error("Failed to update status");
    } finally {
      setActing(false);
    }
  }

  function refreshTimeline() {
    fetchIncidentTimeline(incident.id, { limit: 50 }).then((res) => {
      setTimeline(res.data);
      setNextCursor(res.meta.next_cursor);
    });
  }

  const isLive = ["open", "acknowledged", "investigating"].includes(incident.status);

  return (
    <Dialog open onClose={onClose} static>
      <DialogPanel className="fixed inset-y-0 right-0 m-0 flex h-full w-full max-w-xl flex-col rounded-none bg-white p-0 shadow-2xl">
        {/* Header */}
        <div className="flex items-start justify-between border-b border-gray-200 px-6 py-4">
          <div className="min-w-0 flex-1 pr-4">
            <div className="flex flex-wrap items-center gap-2">
              <Badge color={STATUS_BADGE_COLOR[incident.status] ?? "gray"}>{t(`filter.${incident.status}`)}</Badge>
              <Badge color={SEVERITY_BADGE_COLOR[incident.severity] ?? "gray"}>{t(`filter.${incident.severity}`)}</Badge>
              {incident.priority && (
                <Badge color={PRIORITY_BADGE_COLOR[incident.priority] ?? "gray"}>{incident.priority}</Badge>
              )}
              {incident.is_global && (
                <Badge color="purple"><Globe className="mr-1 h-3 w-3 inline" />{t("drawer.global")}</Badge>
              )}
              {totalFires > 0 && (
                <Badge color="orange"><Bell className="mr-1 h-3 w-3 inline" />{t("drawer.alerted", { count: totalFires })}</Badge>
              )}
              {incident.occurrence_count > 1 && (
                <Badge color="slate">{t("drawer.occurred", { count: incident.occurrence_count })}</Badge>
              )}
              {incident.rca_summary && incident.status === "resolved" && (
                <Badge color="teal">📋 {t("drawer.post_mortem_written")}</Badge>
              )}
              {timeline.some((e) => e.type === "diagnostic_snapshot") && (
                <Badge color="purple">🔬 {t("drawer.diagnostic")}</Badge>
              )}
            </div>
            <h2 className="mt-1.5 text-base font-semibold text-gray-900 leading-snug">
              {incident.title}
            </h2>
            {incident.monitor_name && (
              <p className="mt-0.5 text-xs text-gray-400">{incident.monitor_name}</p>
            )}
          </div>
          <button type="button" onClick={onClose} className="shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Scrollable body — header stays pinned, everything below scrolls.
            min-h-0 is required for the flex child to shrink and scroll. */}
        <div className="min-h-0 flex-1 overflow-y-auto">

        {/* Metadata grid */}
        <div className="border-b border-gray-100 px-6 py-3">
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-xs">
            <div>
              <dt className="text-gray-400">{t("drawer.started")}</dt>
              <dd className="text-gray-700">{fmtTs(incident.started_at)}</dd>
            </div>
            <div>
              <dt className="text-gray-400">{incident.status === "resolved" ? t("drawer.resolved") : t("drawer.duration")}</dt>
              <dd className="text-gray-700">
                {incident.status === "resolved"
                  ? fmtTs(incident.resolved_at)
                  : fmtDuration(
                      (Date.now() - new Date(incident.started_at).getTime()) / 1000,
                      t
                    )}
              </dd>
            </div>
            {incident.failure_reason && (
              <div>
                <dt className="text-gray-400">{t("drawer.reason")}</dt>
                <dd className="text-gray-700">{incident.failure_reason.replace(/_/g, " ")}</dd>
              </div>
            )}
            {incident.server_id && (
              <div>
                <dt className="text-gray-400">Server</dt>
                <dd>
                  <Link
                    to={`/servers/${incident.server_id}`}
                    className="text-teal-600 hover:underline"
                    onClick={() => {}}
                  >
                    View server →
                  </Link>
                </dd>
              </div>
            )}
            {incident.external_id?.startsWith("rum_alert:") && (
              <div>
                <dt className="text-gray-400">RUM</dt>
                <dd>
                  <Link
                    to="/rum"
                    className="text-teal-600 hover:underline"
                    onClick={() => {}}
                  >
                    View RUM →
                  </Link>
                </dd>
              </div>
            )}
            {incident.affected_locations?.length > 0 && (
              <div>
                <dt className="text-gray-400">{t("drawer.locations")}</dt>
                <dd className="flex flex-wrap gap-1">
                  {incident.affected_locations.map((r) => (
                    <span key={r} className="inline-flex items-center gap-0.5 rounded bg-teal-50 px-1.5 py-0.5 font-mono text-teal-600">
                      <MapPin className="h-2.5 w-2.5" />{r}
                    </span>
                  ))}
                </dd>
              </div>
            )}
            {incident.description && (
              <div className="col-span-2">
                <dt className="text-gray-400">{t("drawer.description")}</dt>
                <dd className="text-gray-700">{incident.description}</dd>
              </div>
            )}
          </dl>
        </div>

        {/* Deterministic diagnosis panel */}
        <IncidentDiagnosis incident={incident} />

        {/* Why this alert? */}
        <div className="px-6 pb-3">
          <IncidentExplainer incidentId={incident.id} />
        </div>

        {/* Tags */}
        <TagEditor incident={incident} onUpdated={onUpdated} />

        {/* Responders (command roster) */}
        <ResponderEditor
          incidentId={incident.id}
          responders={responders}
          onAdded={(r) => setResponders((prev) => [...prev, r])}
          onRemoved={(userId) =>
            setResponders((prev) => prev.filter((r) => r.user_id !== userId))
          }
        />

        {/* Stakeholders (external, email-only) */}
        <StakeholderEditor
          incidentId={incident.id}
          stakeholders={stakeholders}
          onAdded={(s) => setStakeholders((prev) => [...prev, s])}
          onRemoved={(stakeholderId) =>
            setStakeholders((prev) => prev.filter((s) => s.id !== stakeholderId))
          }
        />

        {/* Checklist (ad-hoc items or applied from a template) */}
        <ChecklistEditor
          incidentId={incident.id}
          checklist={checklist}
          onChanged={setChecklist}
        />

        {/* Collaboration sessions (bridge link) */}
        <CollaborationEditor
          incidentId={incident.id}
          sessions={collaborationSessions}
          onChanged={setCollaborationSessions}
        />

        {/* Actions */}
        {isLive && (
          <div className="flex gap-2 border-b border-gray-100 px-6 py-3">
            {incident.status === "open" && (
              <Button size="xs" color="amber" disabled={acting} onClick={handleAcknowledge}>
                {t("actions.acknowledge")}
              </Button>
            )}
            {incident.status === "acknowledged" && (
              <Button size="xs" variant="secondary" disabled={acting} onClick={handleUnacknowledge}>
                {t("actions.unacknowledge")}
              </Button>
            )}
            {incident.status !== "investigating" && (
              <Button size="xs" disabled={acting} onClick={() => handleStatusChange("investigating")}>
                {t("actions.investigating")}
              </Button>
            )}
            <Button size="xs" color="emerald" disabled={acting} onClick={handleResolve}>
              {t("actions.resolve")}
            </Button>
          </div>
        )}
        {incident.status === "resolved" && (
          <div className="flex gap-2 border-b border-gray-100 px-6 py-3">
            <Button size="xs" variant="secondary" disabled={acting} onClick={handleReopen}>
              {t("actions.reopen")}
            </Button>
          </div>
        )}

        {/* Post-mortem / RCA (quick note) */}
        <RcaEditor incident={incident} onUpdated={onUpdated} />

        {/* Postmortem (formal document with review/approval + corrective actions) */}
        <PostmortemEditor incident={incident} />

        {/* Timeline */}
        <div className="px-6 py-4">
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-400">
            {t("drawer.timeline")}
          </h3>
          {timeline.length === 0 ? (
            <p className="text-xs text-gray-400">{t("drawer.no_events")}</p>
          ) : (
            <ol className="relative border-l border-gray-200">
              {timeline.map((event) => (
                <li key={event.id} className="mb-4 ml-4">
                  <span className="absolute -left-2 flex h-4 w-4 items-center justify-center text-xs">
                    {EVENT_ICON[event.type] || "•"}
                  </span>
                  <div>
                    <p className="text-xs font-medium text-gray-800">{event.message}</p>
                    <p className="mt-0.5 text-xs text-gray-400">{fmtTs(event.occurred_at)}</p>
                    {event.type === "diagnostic_snapshot" && (
                      <DiagnosticPanel data={event.data} />
                    )}
                    {event.type === "artifact_captured" && (
                      <ArtifactPanel data={event.data} />
                    )}
                    {event.type === "third_party_impact" && (
                      <ImpactPanel data={event.data} />
                    )}
                  </div>
                </li>
              ))}
            </ol>
          )}
          {nextCursor && (
            <button
              type="button"
              onClick={loadMore}
              disabled={loadingMore}
              className="mt-2 text-xs text-teal-600 hover:underline disabled:opacity-50"
            >
              {loadingMore ? t("common:common.loading") : t("drawer.load_more")}
            </button>
          )}

          <AddNoteForm incidentId={incident.id} onAdded={refreshTimeline} />
          <PostStatusUpdateForm incident={incident} onAdded={refreshTimeline} />
        </div>
        </div>
      </DialogPanel>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Incidents list page
// ---------------------------------------------------------------------------

export default function Incidents() {
  const { t } = useTranslation(["incidents", "common"]);
  const [searchParams, setSearchParams] = useSearchParams();
  const serverIdFilter = searchParams.get("server_id") || null;
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("open");
  const [tagFilter, setTagFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  // Analytics — cached and refetched when a mutation succeeds.
  const analyticsQuery = useIncidentAnalytics();
  const analytics = analyticsQuery.data;

  // First page of incidents — cached by filter params, refetched when the
  // filter changes. Subsequent pages still go through fetchIncidents below
  // (cursor pagination would only complicate cache shape for no gain).
  useEffect(() => {
    setLoading(true);
    setNextCursor(null);
    const params = { limit: 30 };
    if (statusFilter) params.status = statusFilter;
    if (tagFilter.trim()) params.tags = tagFilter.trim();
    if (serverIdFilter) params.server_id = serverIdFilter;
    fetchIncidents(params)
      .then((res) => {
        setIncidents(res.data);
        setNextCursor(res.meta.next_cursor);
      })
      .finally(() => setLoading(false));
  }, [statusFilter, tagFilter, serverIdFilter]);

  async function loadMore() {
    if (!nextCursor) return;
    setLoadingMore(true);
    try {
      const params = { limit: 30, cursor: nextCursor };
      if (statusFilter) params.status = statusFilter;
      if (tagFilter.trim()) params.tags = tagFilter.trim();
      if (serverIdFilter) params.server_id = serverIdFilter;
      const res = await fetchIncidents(params);
      setIncidents((prev) => [...prev, ...res.data]);
      setNextCursor(res.meta.next_cursor);
    } finally {
      setLoadingMore(false);
    }
  }

  function handleUpdated(updated) {
    setIncidents((prev) =>
      prev.map((i) => (i.id === updated.id ? updated : i))
    );
    setSelected(updated);
    analyticsQuery.refetch();
  }

  function handleCreated(incident) {
    setIncidents((prev) => [incident, ...prev]);
    analyticsQuery.refetch();
  }

  async function handleQuickAcknowledge(e, incident) {
    e.stopPropagation();
    try {
      const updated = await acknowledgeIncident(incident.id);
      handleUpdated(updated);
      toast.success(t("actions.success_ack"));
    } catch {
      toast.error("Failed to acknowledge");
    }
  }

  async function handleQuickResolve(e, incident) {
    e.stopPropagation();
    try {
      const updated = await resolveIncident(incident.id);
      handleUpdated(updated);
      toast.success(t("actions.success_resolve"));
    } catch {
      toast.error("Failed to resolve");
    }
  }

  const mttr = analytics?.mean_time_to_resolve_seconds;
  const mttrStr = mttr != null ? fmtDuration(mttr, t) : null;

  return (
    <div>
      {/* Page header */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t("title")}</h1>
          <p className="mt-1 text-sm text-gray-500">{t("subtitle")}</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={() => {
              const params = {};
              if (statusFilter) params.status = statusFilter;
              if (tagFilter.trim()) params.tags = tagFilter.trim();
              exportIncidentsCsv(params).catch(() =>
                toast.error("Failed to export incidents")
              );
            }}
          >
            {t("export_csv")}
          </Button>
          <Button color="red" icon={Plus} onClick={() => setShowCreate(true)}>
            {t("new_incident")}
          </Button>
        </div>
      </div>

      {serverIdFilter && (
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-teal-50 border border-teal-200 px-3 py-2 text-sm text-teal-800">
          <span>Showing incidents for one server</span>
          <button
            type="button"
            className="ml-auto text-xs text-teal-600 hover:underline"
            onClick={() => setSearchParams({})}
          >
            Clear filter ×
          </button>
        </div>
      )}

      {/* Analytics strip */}
      {analytics && (
        <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatCard
            label={t("stats.open")}
            value={analytics.open_incidents}
            color={analytics.open_incidents > 0 ? "text-red-600" : "text-green-600"}
          />
          <StatCard label={t("stats.last_30_days")} value={analytics.total_incidents} />
          <StatCard label={t("stats.mttr")} value={mttrStr ?? "—"} sub={t("stats.mttr_sub")} />
          <StatCard
            label={t("stats.longest_outage")}
            value={analytics.longest_incident_seconds != null ? fmtDuration(analytics.longest_incident_seconds, t) : "—"}
          />
        </div>
      )}

      {/* Status filter tabs + tag search */}
      <div className="mb-4 flex items-center justify-between gap-4">
        <TabGroup
          index={STATUS_TABS.findIndex((t) => t.value === statusFilter)}
          onIndexChange={(i) => setStatusFilter(STATUS_TABS[i].value)}
        >
          <TabList>
            {STATUS_TABS.map((tab) => (
              <Tab key={tab.value}>{t(`filter.${tab.value || "all"}`)}</Tab>
            ))}
          </TabList>
        </TabGroup>
        <TextInput
          placeholder={t("filter.tag_placeholder")}
          value={tagFilter}
          onValueChange={setTagFilter}
          className="w-44"
        />
      </div>

      {/* Incidents table */}
      <Card className="p-0">
        {loading ? (
          <p className="px-5 py-8 text-center text-sm text-gray-400">{t("common:common.loading")}</p>
        ) : incidents.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-gray-400">
            {statusFilter === "open"
              ? t("empty.healthy")
              : t("empty.no_found")}
          </p>
        ) : (
          <>
            <div className="overflow-x-auto">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{t("table.title")}</TableHeaderCell>
                  <TableHeaderCell>{t("table.severity")}</TableHeaderCell>
                  <TableHeaderCell>{t("table.started")}</TableHeaderCell>
                  <TableHeaderCell>{t("table.duration")}</TableHeaderCell>
                  <TableHeaderCell>{t("table.locations")}</TableHeaderCell>
                  <TableHeaderCell>{t("table.status")}</TableHeaderCell>
                  <TableHeaderCell></TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {incidents.map((incident) => {
                  const isLive = ["open", "acknowledged", "investigating"].includes(incident.status);
                  const durationSecs = incident.duration_seconds ??
                    (Date.now() - new Date(incident.started_at).getTime()) / 1000;

                  return (
                    <TableRow
                      key={incident.id}
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => setSelected(incident)}
                    >
                      <TableCell className="font-medium text-gray-900">
                        <div className="flex items-center gap-1.5">
                          {incident.is_global && (
                            <Globe className="h-3.5 w-3.5 shrink-0 text-purple-500" title={t("drawer.global")} />
                          )}
                          <span className="truncate max-w-[220px]">{incident.title}</span>
                        </div>
                        {incident.monitor_name && (
                          <p className="mt-0.5 text-xs text-gray-400">{incident.monitor_name}</p>
                        )}
                        {incident.tags?.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {incident.tags.map((tag) => (
                              <Badge key={tag} color="gray" size="xs">{tag}</Badge>
                            ))}
                          </div>
                        )}
                        {incident.rca_summary && incident.status === "resolved" && (
                          <Badge color="teal" size="xs" className="mt-1">📋 {t("rca.title")}</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap items-center gap-1">
                          <Badge color={SEVERITY_BADGE_COLOR[incident.severity] ?? "gray"}>
                            {t(`filter.${incident.severity}`)}
                          </Badge>
                          {incident.priority && (
                            <Badge color={PRIORITY_BADGE_COLOR[incident.priority] ?? "gray"} size="xs">
                              {incident.priority}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs text-gray-500">
                        <div>{fmtRelative(incident.started_at, t)}</div>
                        <div className="text-gray-400">{fmtDate(incident.started_at)}</div>
                      </TableCell>
                      <TableCell className="text-xs text-gray-500">
                        {fmtDuration(durationSecs, t)}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(incident.affected_locations || []).map((r) => (
                            <Badge key={r} color="teal" size="xs">{r}</Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge color={STATUS_BADGE_COLOR[incident.status] ?? "gray"}>
                          {t(`filter.${incident.status}`)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {isLive && (
                          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                            {incident.status === "open" && (
                              <Button
                                size="xs"
                                variant="secondary"
                                color="amber"
                                onClick={(e) => handleQuickAcknowledge(e, incident)}
                              >
                                {t("actions.ack")}
                              </Button>
                            )}
                            <Button
                              size="xs"
                              variant="secondary"
                              color="emerald"
                              onClick={(e) => handleQuickResolve(e, incident)}
                            >
                              {t("actions.resolve")}
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </div>

            {nextCursor && (
              <div className="border-t border-gray-100 px-5 py-3 text-center">
                <Button variant="light" size="xs" onClick={loadMore} disabled={loadingMore}>
                  {loadingMore ? t("common:common.loading") : t("common:common.load_more")}
                </Button>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Detail drawer */}
      {selected && (
        <IncidentDrawer
          incident={selected}
          onClose={() => setSelected(null)}
          onUpdated={handleUpdated}
        />
      )}

      {/* Create modal */}
      {showCreate && (
        <CreateIncidentModal
          onClose={() => setShowCreate(false)}
          onCreated={handleCreated}
        />
      )}
    </div>
  );
}
